<template>
  <div class="main_app">
    <!-- Application header -->
    <header class="header">
      <!-- Navigation bar -->
      <nav class="navbar navbar-expand navbar-dark d-flex justify-content-center">
        <img class="logo" src="./assets/logo.png" alt="Tradel logo">
        <!-- User connected -->
        <section v-if="currentUser" class="navbar-nav ml-auto d-flex align-items-center">
          <article class="header-actions d-flex align-items-center gap-2">
            <button
              v-if="currentRouteName != 'home'"
              type="button"
              class="btn btn-secondary header-action-button"
              title="Retour a l accueil"
              aria-label="Retour a l accueil"
              @click.prevent="home"
            >
              <span class="material-icons">arrow_back</span>
            </button>
            <button
              v-if="currentRouteName != 'profile'"
              type="button"
              class="btn btn-secondary header-action-button"
              title="Profil"
              aria-label="Profil"
              @click.prevent="profile"
            >
              <span class="material-icons">person</span>
            </button>
            <button
              v-if="mobileTreeAvailable"
              type="button"
              class="btn btn-secondary header-action-button"
              :title="mobileTreeOpen ? 'Fermer l arbre de donnees' : 'Ouvrir l arbre de donnees'"
              :aria-label="mobileTreeOpen ? 'Fermer l arbre de donnees' : 'Ouvrir l arbre de donnees'"
              @click.prevent="mobileTreeOpen = !mobileTreeOpen"
            >
              <span class="material-icons">account_tree</span>
            </button>
            <button
              v-if="showMapFiltersButton"
              type="button"
              class="btn btn-secondary header-action-button"
              :title="mapFiltersCollapsed ? 'Afficher les filtres de la carte' : 'Masquer les filtres de la carte'"
              :aria-label="mapFiltersCollapsed ? 'Afficher les filtres de la carte' : 'Masquer les filtres de la carte'"
              @click.prevent="toggleMapFilters"
            >
              <span class="material-icons">{{ mapFiltersCollapsed ? "filter_alt" : "filter_alt_off" }}</span>
            </button>
            <va-button-dropdown
              v-if="isManager"
              class="notification-dropdown header-action-dropdown"
              aria-label="Notifications"
              hide-icon
            >
              <template #label>
                <span class="notification-icon-wrap">
                  <span class="material-icons">notifications</span>
                  <span v-if="unreadNotificationCount" class="notification-badge">
                    {{ unreadNotificationCount > 99 ? '99+' : unreadNotificationCount }}
                  </span>
                </span>
              </template>
              <div class="notification-menu" @click.stop>
                <div class="notification-menu-header">
                  <strong>Notifications</strong>
                  <button
                    v-if="unreadNotificationCount"
                    type="button"
                    class="notification-read-all"
                    @click="markAllNotificationsRead"
                  >
                    Tout marquer comme lu
                  </button>
                </div>
                <p v-if="!notifications.length" class="notification-empty">Aucune notification</p>
                <button
                  v-for="notification in notifications"
                  :key="notification.idNotification"
                  type="button"
                  :class="['notification-item', { unread: !notification.readAt }]"
                  @click="markNotificationRead(notification)"
                >
                  <span class="notification-item-title">{{ notification.title }}</span>
                  <span class="notification-item-message">{{ notification.message }}</span>
                  <span class="notification-item-date">{{ formatNotificationDate(notification.createdAt) }}</span>
                </button>
              </div>
            </va-button-dropdown>
            <va-button-dropdown
              class="align-button header-action-dropdown"
              aria-label="Menu"
              hide-icon
            >
              <template #label>
                <span class="material-icons">menu</span>
              </template>
              <div class="menu-container">
                <div
                  v-for="functionality in currentUser.functionalities?.filter(f => f.Functionality.page) ?? []"
                  :key="functionality"
                  class="menu-card"
                  @click.prevent="open(functionality.Functionality.nameFunctionality, functionality.right)"
                >
                  <div class="menu-card-body">
                    <div class="menu-icon-container">
                      <img
                        v-if="functionality.Functionality.image"
                        :src="'/images/' + functionality.Functionality.image"
                        class="menu-icon-image"
                      >
                    </div>
                    <h5 class="menu-card-title">{{ functionality.Functionality.translation }}</h5>
                  </div>
                </div>
              </div>
            </va-button-dropdown>
            <button
              type="button"
              class="btn btn-secondary header-action-button"
              title="Deconnexion"
              aria-label="Deconnexion"
              @click.prevent="logOut"
            >
              <span class="material-icons">logout</span>
            </button>
          </article>
        </section>
      </nav>
    </header>

    <section class="main">
      <aside
        v-if="treeVisible || mobileTreeVisible"
        :class="[
          'tree-panel',
          {
            hidden: treeVisible && !treePanelOpen,
            'tree-panel--mobile': mobileTreeVisible,
          }
        ]"
      >
        <div class="tree-panel__content">
          <Suspense>
            <DriverTreeViewVue
              :panel-open="treeVisible ? treePanelOpen : mobileTreeOpen"
              @toggle-panel="toggleTreePanel"
              @select="treeSelection"
            />
          </Suspense>
        </div>
      </aside>

      <button
        v-if="mobileTreeVisible"
        type="button"
        class="tree-panel__backdrop"
        aria-label="Fermer l arbre de donnees"
        @click="mobileTreeOpen = false"
      />

      <button
        v-if="treeVisible && !treePanelOpen"
        type="button"
        class="tree-panel__tab"
        aria-label="Deplier l arbre de donnees"
        @click="treePanelOpen = true"
      >
        >
      </button>

      <div
        :class="[
          'content',
          {
            'content--with-tree': treeVisible && treePanelOpen,
            'content--with-tree-tab': treeVisible && !treePanelOpen,
          }
        ]"
      >
        <section><router-view /></section>
      </div>
    </section>

    <footer>
      V2.100
      <button type="button" class="btn btn-secondary" onclick="location.href='mailto:pgh@pgh.fr'">
        Aide
      </button>
    </footer>
  </div>
</template>

<script>
import { Capacitor } from '@capacitor/core';
import DriverTreeViewVue from './components/treeView/DriverTreeView.vue';
import LogService from './services/log.service.js';
import TachographSessionService from './services/tachograph-session.service.js';
import NotificationService from './services/notification.service.js';

export default {
  components: {
    DriverTreeViewVue
  },
  data() {
    return {
      treePanelOpen: true,
      mobileTreeOpen: false,
      mapFiltersCollapsed: false,
      viewportWidth: typeof window === "undefined" ? 1280 : window.innerWidth,
      notifications: [],
      unreadNotificationCount: 0,
      notificationPollTimer: null,
    };
  },
  mounted() {
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.handleViewportResize);
      window.addEventListener("tims-map-filters-state", this.handleMapFiltersState);
      this.handleViewportResize();
    }
    this.refreshNotifications();
    this.notificationPollTimer = setInterval(() => this.refreshNotifications(), 60000);
  },
  beforeUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.handleViewportResize);
      window.removeEventListener("tims-map-filters-state", this.handleMapFiltersState);
    }
    if (this.notificationPollTimer) {
      clearInterval(this.notificationPollTimer);
      this.notificationPollTimer = null;
    }
  },
  watch: {
    currentUser: {
      immediate: true,
      handler() {
        this.resumeActiveDayIfNeeded();
        this.refreshNotifications();
        if (this.treeVisible) {
          this.treePanelOpen = true;
        }
        if (!this.mobileTreeAvailable) {
          this.mobileTreeOpen = false;
        }
      }
    },
    treeVisible(value) {
      if (value) {
        this.treePanelOpen = true;
      }
    },
    isMobileViewport(value) {
      if (!value) {
        this.mobileTreeOpen = false;
      }
    },
    currentRouteName(value) {
      if (value === "driverMonitoring") {
        this.mobileTreeOpen = false;
      }
      if (value !== "driverMonitoring") {
        this.mapFiltersCollapsed = false;
      }
    },
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentRouteName() {
      return this.$route.name;
    },
    isManagerOrAdmin() {
      const roleName = String(this.currentUser?.roleName ?? "").toLowerCase();
      return roleName === "admin" || roleName === "manager";
    },
    isManager() {
      return String(this.currentUser?.roleName ?? "").toLowerCase() === "manager";
    },
    isMobileViewport() {
      return this.viewportWidth <= 768;
    },
    treeAvailable() {
      if (Capacitor.isNativePlatform() || !this.currentUser || this.currentRouteName === "driverMonitoring") {
        return false;
      }

      if (!this.isManagerOrAdmin) {
        return false;
      }

      const functionalities = this.currentUser.functionalities ?? [];
      return functionalities.some(
        (right) => right.Functionality.nameFunctionality === 'dataTree' && right.read
      );
    },
    treeVisible() {
      return this.treeAvailable && !this.isMobileViewport;
    },
    mobileTreeAvailable() {
      return this.treeAvailable && this.isMobileViewport;
    },
    mobileTreeVisible() {
      return this.mobileTreeAvailable && this.mobileTreeOpen;
    },
    showMapFiltersButton() {
      return this.currentRouteName === "driverMonitoring";
    },
  },
  methods: {
    async refreshNotifications() {
      if (!this.isManager) {
        this.notifications = [];
        this.unreadNotificationCount = 0;
        return;
      }
      try {
        const response = await NotificationService.get({ limit: 30 });
        this.notifications = response.data?.items || [];
        this.unreadNotificationCount = Number(response.data?.unreadCount || 0);
      } catch (error) {
        console.error("Unable to load notifications", error);
      }
    },
    async markNotificationRead(notification) {
      if (!notification || notification.readAt) return;
      try {
        const response = await NotificationService.markRead(notification.idNotification);
        notification.readAt = response.data?.readAt || new Date().toISOString();
        this.unreadNotificationCount = Math.max(0, this.unreadNotificationCount - 1);
      } catch (error) {
        console.error("Unable to mark notification as read", error);
      }
    },
    async markAllNotificationsRead() {
      try {
        await NotificationService.markAllRead();
        const readAt = new Date().toISOString();
        this.notifications.forEach(notification => {
          if (!notification.readAt) notification.readAt = readAt;
        });
        this.unreadNotificationCount = 0;
      } catch (error) {
        console.error("Unable to mark notifications as read", error);
      }
    },
    formatNotificationDate(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return new Intl.DateTimeFormat("fr-FR", {
        dateStyle: "short",
        timeStyle: "short"
      }).format(date);
    },
    handleViewportResize() {
      this.viewportWidth = typeof window === "undefined" ? 1280 : window.innerWidth;
    },
    handleMapFiltersState(event) {
      this.mapFiltersCollapsed = Boolean(event?.detail?.collapsed);
    },
    toggleMapFilters() {
      if (typeof window === "undefined") {
        return;
      }
      window.dispatchEvent(new CustomEvent("tims-map-filters-toggle"));
    },
    toggleTreePanel() {
      if (this.treeVisible) {
        this.treePanelOpen = !this.treePanelOpen;
        return;
      }

      if (this.mobileTreeAvailable) {
        this.mobileTreeOpen = !this.mobileTreeOpen;
      }
    },
    resumeActiveDayIfNeeded() {
      if (!TachographSessionService.hasStoredDay() || this.currentUser?.roleType !== 'driver') {
        return;
      }

      if (this.currentUser.debug) {
        LogService.create(JSON.stringify({ content: "Launch tachograph view on restart ?", more: "App# starting" }));
      }

      if (this.$route.path !== '/tachograph') {
        this.$router.push('/tachograph');
      }
    },
    logOut() {
      this.$store.dispatch('auth/logout');
      this.$router.push('/login');
    },
    profile() {
      this.$router.push('/profile');
    },
    home() {
      this.$router.push('/home');
    },
    open(functionnality, right) {
      this.$router.push({ name: functionnality, params: { right: right } });
    },
    treeSelection(item) {
      this.$store.dispatch("context/treeSelectionChanged", item);
      if (this.mobileTreeVisible) {
        this.mobileTreeOpen = false;
      }
      if (item.type === "driver") {
        this.$router.push('/driver');
      }
      else if (item.type === "home") {
        this.$router.push('/home');
      }
      else if (item.type === "company") {
        this.$router.push('/company');
      }
      else if (item.type === "agency") {
        this.$router.push('/agency');
      }
      else if (item.type === "vehicle") {
        this.$router.push('/vehicle');
      }
    }
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Source+Sans+Pro:ital,wght@0,400;1,700&display=swap');
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

.main {
  --tree-panel-width: min(340px, calc(100vw - 32px));
  --tree-panel-tab-width: 30px;
  --tree-panel-top: 92px;
  min-height: 80vh;
  position: relative;
  background: #ffffff;
}

.menu {
  padding-top: env(safe-area-inset-top);
  overflow: auto;
  max-height: 80vh;
}

.header-actions {
  gap: 0.55rem;
}

.header-action-button {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.header-action-button .material-icons,
.header-action-dropdown .material-icons {
  font-size: 1.15rem;
}

.header-action-dropdown :deep(.va-button-dropdown__content) {
  min-width: auto;
}

.header-action-dropdown :deep(.va-button) {
  min-width: 40px;
  width: 40px;
  height: 40px;
  padding: 0;
}

.header-action-dropdown :deep(.va-button__content) {
  justify-content: center;
}

.notification-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.notification-badge {
  position: absolute;
  top: -9px;
  right: -12px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border: 2px solid #6c757d;
  border-radius: 9px;
  background: #dc2626;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
}

.notification-menu {
  width: min(380px, calc(100vw - 24px));
  max-height: min(520px, calc(100vh - 120px));
  overflow-y: auto;
  background: #ffffff;
  color: #111827;
}

.notification-menu-header {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
}

.notification-read-all {
  border: 0;
  background: transparent;
  color: #2563eb;
  font-size: 12px;
  cursor: pointer;
}

.notification-empty {
  margin: 0;
  padding: 22px 14px;
  color: #64748b;
  text-align: center;
}

.notification-item {
  width: 100%;
  display: grid;
  gap: 4px;
  padding: 12px 14px;
  border: 0;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  color: #111827;
  text-align: left;
  cursor: pointer;
}

.notification-item.unread {
  border-left: 3px solid #2563eb;
  background: #eff6ff;
}

.notification-item-title {
  font-size: 14px;
  font-weight: 700;
}

.notification-item-message {
  color: #334155;
  font-size: 13px;
  line-height: 1.35;
}

.notification-item-date {
  color: #64748b;
  font-size: 11px;
}

.va-dropdown__content-wrapper {
  z-index: 1000 !important;
}

.menu-icon-container {
  width: 30px;
  height: 30px;
  padding-right: 5px;
}

.menu-icon-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
}

.menu-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 5px;
  margin: 5px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.menu-card-body {
  display: flex;
  flex-direction: row;
}

.menu-card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  background: lightgray;
  transform: translateY(-4px);
  transition: all 0.2s ease-in-out;
}

.menu-card-title {
  font-size: 12px;
  margin: auto;
}

.menu-cards-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 16px;
  padding: 16px;
}

.tree-panel {
  position: fixed;
  top: var(--tree-panel-top);
  left: 0;
  bottom: 0;
  z-index: 950;
  width: var(--tree-panel-width);
  border: 0;
  border-right: 1px solid #d9dee8;
  border-radius: 0;
  background: #ffffff;
  box-shadow: none;
  overflow: visible;
  transition: transform 0.25s ease;
}

.tree-panel--mobile {
  width: min(88vw, 340px);
  z-index: 1100;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.2);
  transform: translateX(0);
}

.tree-panel.hidden {
  transform: translateX(calc(var(--tree-panel-width) * -1));
}

.tree-panel__content {
  height: 100%;
  padding: 0;
  overflow-y: auto;
}

.tree-panel__tab {
  position: fixed;
  top: calc(var(--tree-panel-top) + 12px);
  left: 0;
  z-index: 951;
  width: var(--tree-panel-tab-width);
  height: 38px;
  border: 1px solid #d9dee8;
  border-left: 0;
  background: #ffffff;
  color: #4b5563;
  font-size: 0.92rem;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;
}

.tree-panel__tab:hover {
  background: #f8fafc;
  color: #111827;
}

.tree-panel__backdrop {
  position: fixed;
  inset: var(--tree-panel-top) 0 0 0;
  z-index: 1090;
  border: 0;
  background: rgba(15, 23, 42, 0.22);
  cursor: pointer;
}

.content {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  transition: margin-left 0.25s ease, width 0.25s ease;
}

.content--with-tree {
  width: calc(100% - var(--tree-panel-width));
  margin-left: var(--tree-panel-width);
}

.content--with-tree-tab {
  width: calc(100% - var(--tree-panel-tab-width));
  margin-left: var(--tree-panel-tab-width);
}

@media (max-width: 1180px) {
  .main {
    --tree-panel-width: min(320px, calc(100vw - 24px));
  }
}

@media (max-width: 768px) {
  .main {
    --tree-panel-top: 84px;
  }

  .header-actions {
    gap: 0.4rem;
  }

  .header-action-button {
    width: 36px;
    height: 36px;
  }

  .header-action-dropdown :deep(.va-button) {
    min-width: 36px;
    width: 36px;
    height: 36px;
  }

  .tree-panel {
    top: var(--tree-panel-top);
    bottom: 0;
  }

  .content--with-tree {
    width: 100%;
    margin-left: 0;
  }

  .content--with-tree-tab {
    width: 100%;
    margin-left: 0;
  }
}
</style>
